import { useAuth } from '@hb/react'
import { arrayToObject, Assessment, objectToArray, PopulatedAssessment, WithId } from '@hb/shared'
import { createContext, PropsWithChildren, useContext, useMemo } from 'react'
import { usePopulatedAssessment } from '../../hooks/backend/user/usePopulatedAssessment'
import { usePopulatedUser } from '../../hooks/backend/user/usePopulatedUser'
import { useUserAssessments } from '../../hooks/backend/user/useUserAssessments'
import { PopulatedUserData } from '../../types/populated'

interface UserAssessmentContextData {
  userId: string | null
  assessments: Record<string, Assessment>
  assessmentsArr: WithId<Assessment>[]
  archivedAssessments: Record<string, Assessment>
  archivedAssessmentsArr: WithId<Assessment>[]
  allAssessments: Record<string, Assessment>
  allAssessmentsArr: WithId<Assessment>[]
  loading: boolean
  error: string | null
  user: PopulatedUserData
  mostRecent: PopulatedAssessment | null
  mostRecentId: string | null
}
export const UserAssessmentsContext = createContext<UserAssessmentContextData>({
  assessments: {},
  assessmentsArr: [],
  archivedAssessments: {},
  archivedAssessmentsArr: [],
  allAssessments: {},
  allAssessmentsArr: [],
  loading: false,
  error: null,
  userId: null,
  user: {
    adminPatientRef: null,
    user: null,
    loading: false,
    isInvite: false,
    patientRef: null,
    practicePatientRef: null,
    adminItem: null,
  },
  mostRecent: null,
  mostRecentId: null,
})

export const UserAssessmentsProvider = ({ children }: PropsWithChildren) => {
  const authUser = useAuth(s => s.authUser)
  const { data: assessments, error, loading } = useUserAssessments(authUser?.uid, true)

  const {
    unarchivedAssessments,
    unarchivedAssessmentsArr,
    archivedAssessments,
    archivedAssessmentsArr,
    allAssessments,
    allAssessmentsArr,
  } = useMemo(() => {
    const allAssessmentsArr = objectToArray(assessments ?? {})
    const partitioned = objectToArray(assessments ?? {}).reduce(
      (acc, assessment) => {
        if (assessment.archivedOn) {
          acc.archivedAssessments.push(assessment)
        } else {
          acc.unarchivedAssessments.push(assessment)
        }
        return acc
      },
      {
        unarchivedAssessments: [] as WithId<Assessment>[],
        archivedAssessments: [] as WithId<Assessment>[],
      },
    )

    return {
      unarchivedAssessments: arrayToObject(partitioned.unarchivedAssessments) ?? {},
      unarchivedAssessmentsArr: partitioned.unarchivedAssessments,
      archivedAssessments: arrayToObject(partitioned.archivedAssessments) ?? {},
      archivedAssessmentsArr: partitioned.archivedAssessments,
      allAssessments: assessments ?? {},
      allAssessmentsArr,
    }
  }, [assessments])
  const mostRecentAssessment = useMemo(() => {
    const mostRecent = unarchivedAssessmentsArr.sort((a, b) =>
      a.createdOn < b.createdOn ? 1 : -1,
    )[0]
    return mostRecent ? mostRecent : undefined
  }, [unarchivedAssessmentsArr])

  const userData = usePopulatedUser(authUser?.uid)
  const { user, loading: userLoading } = userData
  const mostRecent = usePopulatedAssessment(mostRecentAssessment, user)
  const contextData = useMemo(
    () => ({
      userId: authUser?.uid ?? null,
      assessments: unarchivedAssessments ?? {},
      assessmentsArr: unarchivedAssessmentsArr,
      archivedAssessments,
      archivedAssessmentsArr,
      allAssessments,
      allAssessmentsArr,
      loading: loading || userLoading,
      user: userData,
      error,
      mostRecent,
      mostRecentId: mostRecentAssessment?.id ?? null,
    }),
    [
      unarchivedAssessments,
      unarchivedAssessmentsArr,
      loading,
      archivedAssessments,
      archivedAssessmentsArr,
      allAssessments,
      allAssessmentsArr,
      error,
      mostRecent,
      userLoading,
      userData,
      authUser,
      mostRecentAssessment,
    ],
  )
  return (
    <UserAssessmentsContext.Provider value={contextData}>
      {children}
    </UserAssessmentsContext.Provider>
  )
}

export const useMyAssessments = () => useContext(UserAssessmentsContext)
